import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { format, startOfMonth, endOfMonth, addMonths, isSameDay } from "date-fns";
import { toast } from "react-toastify";

import CalendarMonthGrid from "../Components/CalendarMonthGrid";
import AppointmentFormModal from "../Components/AppointmentFormModal";
import { appointmentsApi } from "../Components/appointmentsApi";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import { appointmentStatusMeta } from "../Components/appointmentsUiUtils";

const theme = createTheme({
  palette: {
    primary: { main: "#5e72e4" },
    background: { default: "#f8f9fe" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 800, letterSpacing: "-1px" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
});

export default function AppointmentsCalendarPage() {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [types, setTypes] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaults, setCreateDefaults] = useState({});
  const [selectedCounsellor, setSelectedCounsellor] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);

  const monthRange = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }, [calendarMonth]);

  const loadTypes = useCallback(async () => {
    const data = await appointmentsApi.getTypes();
    setTypes(data?.appointment_types || []);
  }, []);

  const loadCounsellors = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${url}/get_users`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) return;
      const data = await response.json();
      setCounsellors(
        (Array.isArray(data) ? data : []).filter((item) =>
          ["Counsellor", "Visa Counsellor", "Visa&Application Counsellor", "Manager", "admin"].includes(item.role)
        )
      );
    } catch (_error) {
      setCounsellors([]);
    }
  }, []);

  const loadAppointments = useCallback(
    async (range, counsellor) => {
      if (!range?.start || !range?.end) return;
      const data = await appointmentsApi.listAppointments({
        start_date: range.start,
        end_date: range.end,
        counsellor_id: counsellor || undefined,
        per_page: 200,
        page: 1,
      });
      setAppointments(data?.appointments || []);
    },
    []
  );

  useEffect(() => {
    loadTypes();
    loadCounsellors();
  }, [loadTypes, loadCounsellors]);

  useEffect(() => {
    loadAppointments(monthRange, selectedCounsellor);
  }, [monthRange.start, monthRange.end, selectedCounsellor, loadAppointments]);

  const goMonth = useCallback((direction) => {
    setCalendarMonth((prev) => addMonths(prev, direction));
  }, []);

  const appointmentsForDay = useCallback(
    (day) => {
      return appointments.filter((apt) => {
        const start = apt.start_time_utc ? new Date(apt.start_time_utc) : null;
        return start && isSameDay(start, day);
      });
    },
    [appointments]
  );

  const getEventColor = useCallback((apt) => {
    return apt.appointment_type?.color || appointmentStatusMeta[apt.status]?.color || "#2563eb";
  }, []);

  const renderEvent = useCallback(
    (apt) => (
      <Typography
        key={apt.id}
        variant="caption"
        noWrap
        sx={{
          fontSize: "0.7rem",
          bgcolor: alpha(getEventColor(apt), 0.25),
          color: "text.primary",
          px: 0.5,
          py: 0.25,
          borderRadius: 1,
          borderLeft: `3px solid ${getEventColor(apt)}`,
        }}
      >
        {apt.appointment_type?.name || "Appointment"} — {apt.customer_name || apt.customer_email || "—"}
      </Typography>
    ),
    [getEventColor]
  );

  const onDayClick = useCallback((day) => {
    setSelectedDay(day);
    setDayDrawerOpen(true);
  }, []);

  const appointmentsOnSelectedDay = useMemo(
    () => (selectedDay ? appointmentsForDay(selectedDay) : []),
    [selectedDay, appointmentsForDay]
  );

  const openCreateForDay = useCallback(() => {
    if (!selectedDay) return;
    const start = new Date(selectedDay);
    start.setHours(9, 0, 0, 0);
    setCreateDefaults({
      start_time: format(start, "yyyy-MM-dd'T'HH:mm"),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      counsellor_id: selectedCounsellor || "",
    });
    setDayDrawerOpen(false);
    setShowCreate(true);
  }, [selectedDay, selectedCounsellor]);

  const onCreate = useCallback(
    async (payload) => {
      const data = await appointmentsApi.createAppointment(payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setShowCreate(false);
      toast.success("Appointment created.");
      loadAppointments(monthRange, selectedCounsellor);
    },
    [loadAppointments, monthRange, selectedCounsellor]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "var(--bg-page, #f4f6f8)",
          backgroundImage: "linear-gradient(180deg, var(--bg-page, #f4f6f8) 0%, var(--bg-page-secondary, #eef1f5) 100%)",
        }}
      >
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ p: { xs: 2, md: 3 } }}>
            <Container maxWidth="xl" disableGutters>
              <Stack spacing={3}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-1px", color: "var(--header-color, #111827)" }}>
                        Appointments Calendar
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, color: "var(--header-accent, #4c5fd6)" }}>
                        Full month view — click a day to see appointments or create one.
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 180 }} variant="outlined">
                      <InputLabel>Counsellor</InputLabel>
                      <Select
                        value={selectedCounsellor}
                        onChange={(e) => setSelectedCounsellor(e.target.value)}
                        label="Counsellor"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">All Counsellors</MenuItem>
                        {counsellors.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)} sx={{ borderRadius: 3 }}>
                      Create Appointment
                    </Button>
                  </Stack>
                </Box>

                <CalendarMonthGrid
                  calendarMonth={calendarMonth}
                  onMonthChange={goMonth}
                  onDayClick={onDayClick}
                  eventsForDay={appointmentsForDay}
                  getEventColor={getEventColor}
                  renderEvent={renderEvent}
                  minDayHeight={100}
                />
              </Stack>
            </Container>
          </Grid>
        </Grid>

        <Drawer
          anchor="right"
          open={dayDrawerOpen}
          onClose={() => setDayDrawerOpen(false)}
          PaperProps={{ sx: { width: { xs: "100%", sm: 360 }, p: 2 } }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedDay ? format(selectedDay, "EEEE, MMM d") : ""}
              </Typography>
              <IconButton onClick={() => setDayDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateForDay} fullWidth sx={{ borderRadius: 2 }}>
              Add appointment this day
            </Button>
            <Typography variant="subtitle2" color="text.secondary">
              Appointments
            </Typography>
            {appointmentsOnSelectedDay.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No appointments. Click &quot;Add appointment this day&quot; to create one.
              </Typography>
            ) : (
              <List dense disablePadding>
                {appointmentsOnSelectedDay.map((apt) => (
                  <ListItemButton
                    key={apt.id}
                    onClick={() => toast.info(`${apt.customer_name || apt.customer_email} — ${apt.status}`)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      borderLeft: `4px solid ${getEventColor(apt)}`,
                    }}
                  >
                    <ListItemText
                      primary={apt.appointment_type?.name || "Appointment"}
                      secondary={
                        <>
                          {apt.customer_name || apt.customer_email} • {apt.start_time_utc ? format(new Date(apt.start_time_utc), "HH:mm") : ""} • {apt.status}
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Stack>
        </Drawer>

        <AppointmentFormModal show={showCreate} onHide={() => setShowCreate(false)} types={types} counsellors={counsellors} initialValues={createDefaults} onSubmit={onCreate} />
      </Box>
    </ThemeProvider>
  );
}
